/** 在可编辑容器内按纯文本查找并选中（用于 AI 写作插入前恢复选区） */
function collapseWs(text: string): string {
  return text.replace(/\u200b/g, '').replace(/\s+/g, ' ').trim()
}

/** Markdown 选区转 DOM 可见纯文本（IR 模式下 DOM 不含 # 等标记） */
function stripMarkdownForMatch(text: string): string {
  return text
    .split('\n')
    .map((line) =>
      line
        .replace(/^#{1,6}\s+/, '')
        .replace(/^>\s+/, '')
        .replace(/^[-*+]\s+/, '')
        .replace(/^\d+\.\s+/, '')
        .replace(/`([^`]+)`/g, '$1')
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/\*([^*]+)\*/g, '$1')
        .replace(/~~([^~]+)~~/g, '$1')
        .trim(),
    )
    .filter(Boolean)
    .join('\n')
}

function collectTextNodes(container: HTMLElement) {
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT)
  const nodes: Text[] = []
  let combined = ''
  let node = walker.nextNode() as Text | null
  while (node) {
    nodes.push(node)
    combined += node.data
    node = walker.nextNode() as Text | null
  }
  return { nodes, combined }
}

function selectRangeAtIndex(nodes: Text[], idx: number, length: number, container: HTMLElement): boolean {
  let startNode: Text | null = null
  let startOffset = 0
  let endNode: Text | null = null
  let endOffset = 0
  let pos = 0
  for (const n of nodes) {
    const len = n.data.length
    if (startNode === null && pos + len > idx) {
      startNode = n
      startOffset = idx - pos
    }
    if (endNode === null && pos + len >= idx + length) {
      endNode = n
      endOffset = idx + length - pos
      break
    }
    pos += len
  }
  if (!startNode || !endNode) return false

  const range = document.createRange()
  range.setStart(startNode, startOffset)
  range.setEnd(endNode, endOffset)
  const sel = window.getSelection()
  if (!sel) return false
  sel.removeAllRanges()
  sel.addRange(range)

  const focusTarget =
    container.closest('.vditor-ir pre') ??
    container.querySelector('[contenteditable="true"]') ??
    container
  if (focusTarget instanceof HTMLElement) {
    focusTarget.focus({ preventScroll: true })
  }

  const rect = range.getBoundingClientRect()
  if (rect.width || rect.height) {
    const el =
      startNode.nodeType === Node.TEXT_NODE ? startNode.parentElement : (startNode as unknown as Element)
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }
  return true
}

/** 忽略空白差异，在 combined 中定位 normNeedle 并选中 */
function selectNormalizedMatch(
  combined: string,
  nodes: Text[],
  normNeedle: string,
  container: HTMLElement,
): boolean {
  if (!normNeedle) return false

  let norm = ''
  const indexMap: number[] = []
  for (let i = 0; i < combined.length; i += 1) {
    const ch = combined[i]
    if (/\s/.test(ch)) {
      if (norm.endsWith(' ')) continue
      if (norm.length === 0) continue
      norm += ' '
      indexMap.push(i)
    } else {
      norm += ch
      indexMap.push(i)
    }
  }

  const trimmed = norm.trim()
  const lead = norm.length - norm.trimStart().length
  const idx = trimmed.indexOf(normNeedle)
  if (idx < 0) return false

  const startNorm = lead + idx
  const endNorm = startNorm + normNeedle.length - 1
  const startCombined = indexMap[startNorm] ?? 0
  const endCombined = (indexMap[endNorm] ?? startCombined) + 1
  return selectRangeAtIndex(nodes, startCombined, endCombined - startCombined, container)
}

function uniqueNeedles(text: string): string[] {
  const raw = text.replace(/\u200b/g, '')
  const trimmed = raw.trim()
  const plain = stripMarkdownForMatch(raw)
  const needles = [raw, trimmed, plain, stripMarkdownForMatch(trimmed)]
  if (trimmed.length > 240) {
    needles.push(trimmed.slice(0, 240), plain.slice(0, 240))
  }
  return [...new Set(needles.filter((n) => n.length > 0))]
}

export function selectTextInContainer(container: HTMLElement | null, text: string): boolean {
  if (!container) return false
  const { nodes, combined } = collectTextNodes(container)
  if (!combined) return false

  for (const needle of uniqueNeedles(text)) {
    const idx = combined.indexOf(needle)
    if (idx >= 0) {
      return selectRangeAtIndex(nodes, idx, needle.length, container)
    }
    const normNeedle = collapseWs(needle)
    if (normNeedle.length >= 2 && selectNormalizedMatch(combined, nodes, normNeedle, container)) {
      return true
    }
  }
  return false
}
